import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { ReactComponent as PlusIcon } from "feather-icons/dist/icons/plus.svg";
import { ReactComponent as MinusIcon } from "feather-icons/dist/icons/minus.svg";
import WordSpacedText from "../WordSpacedText";

const Container = tw.div`relative font-[CustomFont]`;
const Content = tw.div`max-w-screen-xl mx-auto py-16 lg:py-20`;
const TwoColumn = tw.div`flex flex-col lg:flex-row items-center`;
const Column = tw.div`w-full lg:w-1/2`;

const Image = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  props.imageContain ? tw`bg-contain bg-no-repeat` : tw`bg-no-repeat`,
  props.imageShadow ? tw`shadow` : tw`shadow-none`,
  tw`hidden lg:block rounded h-144`,
  !props.imageContain &&
    `
    background-size: 100% 100%; /* full width, height auto */
    background-position: top center; /* top aligned vertically, centered horizontally */
  `,
]);

const FAQContent = tw.div`lg:ml-12`;
const Subheading = tw(
  SubheadingBase
)`mb-4 text-center lg:text-left text-primary-600`;
const Heading = styled(SectionHeading)`
  ${tw`lg:text-center text-2xl lg:text-3xl xl:text-4xl text-center`}
  font-family: 'CustomFont', sans-serif;
  color: #c78a41;
  direction: rtl;
`;

const Description = styled.p`
  ${tw`max-w-xl text-center mx-auto lg:mx-auto lg:text-center lg:max-w-none leading-relaxed mt-4 text-secondary-100 text-xl lg:text-2xl xl:text-3xl`}
  font-family: 'CustomFont', sans-serif;
  direction: rtl;
`;

const FAQSContainer = tw.dl`mt-12`;
const FAQ = tw.div`cursor-pointer mt-8 select-none border lg:border-0 px-8 py-4 lg:p-0 rounded-lg lg:rounded-none`;
const Question = tw.dt`flex justify-between items-center flex-row-reverse`;

const QuestionText = styled.span`
  ${tw`text-xl lg:text-2xl xl:text-3xl font-semibold text-right`}
  font-family: 'CustomFont', sans-serif;
  color: #2f5e4e; /* green */
  direction: rtl;
`;

// const QuestionToggleIcon = styled.span`
//   background-color: #C78A41;; /* orange */
//   ${tw`mr-2 p-1 rounded-full transition duration-300 flex items-center justify-center`}
//   svg {
//     ${tw`w-4 h-4`}
//   }
// `;
const QuestionToggleIcon = styled.span`
  background-color: #c78a41; /* orange */
  color: white; /* make plus/minus white */
  ${tw`mr-2 p-1 rounded-full transition duration-300 flex items-center justify-center`}
  svg {
    ${tw`w-4 h-4`}
    stroke: currentColor; /* ensure SVG stroke uses white */
  }
`;

const Answer = motion(styled.dd`
  ${tw`text-left text-lg lg:text-xl xl:text-2xl`}
  line-height: 1.8;
  font-family: "CustomFont", sans-serif;
  direction: ltr;
  padding-left: 2.5rem;
`);

export default ({
  subheading = "",
  heading = "About Jamia",
  description = "جامعہ ایک دینی ادارہ ہے جو تعلیم، تربیت اور دعوت کے میدان میں سرگرم ہے۔",
  imageSrc = process.env.PUBLIC_URL + "/logo512.png",
  imageContain = false,
  imageShadow = true,
  faqs = null,
}) => {
  const defaultFaqs = [
    {
      question: "کیا جامعہ میں قیام و طعام کی سہولت ہے؟",
      answer:
        "جی ہاں، جامعہ میں طلبہ کے لیے مفت قیام و طعام کی سہولت موجود ہے۔",
    },
    {
      question: "جامعہ میں کس معیار کی تعلیم دی جاتی ہے؟",
      answer:
        "جامعہ میں قرآن، حدیث، فقہ، عربی ادب، اور دیگر اسلامی علوم کی معیاری تعلیم فراہم کی جاتی ہے۔",
    },
    {
      question: "کیا غیر ملکی طلبہ کو بھی داخلہ دیا جاتا ہے؟",
      answer: "جی ہاں، جامعہ میں بیرونِ ملک سے بھی طلبہ داخلہ لیتے ہیں۔",
    },
  ];

  if (!faqs || faqs.length === 0) faqs = defaultFaqs;

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = (questionIndex) => {
    setActiveQuestionIndex(
      activeQuestionIndex === questionIndex ? null : questionIndex
    );
  };

  return (
    <Container>
      <Content>
        <TwoColumn>
          <Column>
            <Image
              imageContain={imageContain}
              imageShadow={imageShadow}
              imageSrc={imageSrc}
            />
          </Column>
          <Column>
            <FAQContent>
              {subheading && <Subheading>{subheading}</Subheading>}
              <WordSpacedText>
                <Heading>{heading}</Heading>
              </WordSpacedText>
              <Description>{description}</Description>
              <FAQSContainer>
                {faqs.map((faq, index) => (
                  <FAQ
                    key={index}
                    onClick={() => toggleQuestion(index)}
                    className="group"
                  >
                    <Question>
                      <QuestionText>{faq.question}</QuestionText>
                      <QuestionToggleIcon>
                        {activeQuestionIndex === index ? (
                          <MinusIcon />
                        ) : (
                          <PlusIcon />
                        )}
                      </QuestionToggleIcon>
                    </Question>
                    <Answer
                      variants={{
                        open: { opacity: 1, height: "auto", marginTop: "16px" },
                        collapsed: { opacity: 0, height: 0, marginTop: "0px" },
                      }}
                      initial="collapsed"
                      animate={
                        activeQuestionIndex === index ? "open" : "collapsed"
                      }
                      transition={{
                        duration: 0.3,
                        ease: [0.04, 0.62, 0.23, 0.98],
                      }}
                    >
                      {faq.answer}
                    </Answer>
                  </FAQ>
                ))}
              </FAQSContainer>
            </FAQContent>
          </Column>
        </TwoColumn>
      </Content>
    </Container>
  );
};
